import React, { useState } from 'react';
import { useOnboarding } from './OnboardingContext';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Building2, DollarSign, AlertTriangle, CheckCircle2, Info,
  Calculator, TrendingUp, PiggyBank, ArrowRight, Lightbulb,
  HelpCircle, ChevronRight, X, Sparkles
} from 'lucide-react';

/**
 * SCorpGuide - Comprehensive S-Corp guidance for reasonable salary and payroll
 * Only displays for users with business_type === 's_corp'
 */
const SCorpGuide = ({ className = '' }) => {
  const { user } = useAuth();
  const { 
    showChecklist, 
    isSubstepCompleted, 
    completeSubstep,
    workflow 
  } = useOnboarding();
  
  const [activeTab, setActiveTab] = useState('salary');
  const [dismissed, setDismissed] = useState(false);

  // Only show for S-Corp users
  if (!user || user.business_type !== 's_corp') {
    return null;
  }

  // Check if S-Corp step exists in workflow
  const scorpStep = workflow.find(s => s.id === 'scorp_payroll');
  if (!scorpStep || !showChecklist || dismissed) {
    return null;
  }

  // Check completion status
  const salaryCompleted = isSubstepCompleted('scorp_payroll', 'reasonable_salary');
  const optimizerCompleted = isSubstepCompleted('scorp_payroll', 'review_optimizer');
  const planningCompleted = isSubstepCompleted('scorp_payroll', 'set_salary');
  const allCompleted = salaryCompleted && optimizerCompleted && planningCompleted;

  if (allCompleted) {
    return null;
  }

  const handleComplete = async (substepId) => {
    await completeSubstep('scorp_payroll', substepId);
  };

  return (
    <Card className={`border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 ${className}`}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                S-Corp Payroll Planning
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                  Important
                </Badge>
              </CardTitle>
              <CardDescription>
                Optimize your salary vs. distributions for maximum tax savings
              </CardDescription>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="h-8 w-8 p-0 text-amber-600 hover:text-amber-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 bg-amber-100/50">
            <TabsTrigger 
              value="salary" 
              className="data-[state=active]:bg-white data-[state=active]:text-amber-700"
            >
              <span className="flex items-center gap-1">
                {salaryCompleted && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                Reasonable Salary
              </span>
            </TabsTrigger>
            <TabsTrigger 
              value="optimizer"
              className="data-[state=active]:bg-white data-[state=active]:text-amber-700"
            >
              <span className="flex items-center gap-1">
                {optimizerCompleted && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                Optimizer
              </span>
            </TabsTrigger>
            <TabsTrigger 
              value="planning"
              className="data-[state=active]:bg-white data-[state=active]:text-amber-700"
            >
              <span className="flex items-center gap-1">
                {planningCompleted && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                Planning
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Reasonable Salary */}
          <TabsContent value="salary" className="mt-4 space-y-4">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">IRS Requirement</h4>
                  <p className="text-sm text-gray-600">
                    S-Corp owners who perform services <strong>must</strong> pay themselves a 
                    "reasonable salary" before taking any distributions. This is not optional.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>What counts as reasonable?</strong> Typically 40-60% of net profits, 
                    based on industry standards and your role in the company.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Factors considered:</strong> Training, experience, duties performed, 
                    time devoted, comparable salaries in your industry.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    <strong>Audit risk:</strong> Paying yourself too little triggers IRS scrutiny. 
                    They can reclassify distributions as wages and assess back taxes + penalties.
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-amber-800 text-sm">Rule of Thumb</span>
                </div>
                <p className="text-sm text-amber-700">
                  Start with at least 40% of your net business income as salary. If you're 
                  heavily involved in operations, consider 50-60%. When in doubt, pay more 
                  in salary—it's safer than facing an IRS audit.
                </p>
              </div>
            </div>

            <Button 
              onClick={() => handleComplete('reasonable_salary')}
              disabled={salaryCompleted}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              {salaryCompleted ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</>
              ) : (
                <>I Understand the Requirements <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </TabsContent>

          {/* Tab 2: S-Corp Optimizer */}
          <TabsContent value="optimizer" className="mt-4 space-y-4">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Company Structure Optimizer</h4>
                  <p className="text-sm text-gray-600">
                    Use our built-in tool to see potential tax savings from different 
                    salary/distribution splits.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Salary (W-2)</p>
                    <p className="font-semibold text-gray-900 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      Subject to payroll tax
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      15.3% FICA (Social Security + Medicare)
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">Distributions</p>
                    <p className="font-semibold text-emerald-600 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      No payroll tax
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Only income tax applies
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-800">
                    <strong>Example:</strong> On $100,000 profit with a $50,000 salary, 
                    you save ~$7,650 in self-employment tax compared to a sole proprietorship 
                    where all $100,000 would be subject to SE tax.
                  </p>
                </div>

                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Info className="w-4 h-4 text-blue-500" />
                  <span>
                    Find the optimizer on Dashboard → Company Structure Optimizer card
                  </span>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => handleComplete('review_optimizer')}
              disabled={optimizerCompleted}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {optimizerCompleted ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</>
              ) : (
                <>I've Reviewed the Optimizer <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </TabsContent>

          {/* Tab 3: Salary Planning */}
          <TabsContent value="planning" className="mt-4 space-y-4">
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <PiggyBank className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Plan Your Salary Strategy</h4>
                  <p className="text-sm text-gray-600">
                    Balance tax savings with compliance and retirement planning.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <h5 className="font-medium text-emerald-800 mb-2 flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" /> Benefits of Higher Salary
                  </h5>
                  <ul className="text-sm text-emerald-700 space-y-1">
                    <li>• Higher 401(k) contribution limits (based on W-2 wages)</li>
                    <li>• More Social Security credits for retirement</li>
                    <li>• Lower IRS audit risk</li>
                    <li>• Better documentation for loans/mortgages</li>
                  </ul>
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" /> Benefits of More Distributions
                  </h5>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Save 15.3% on amounts above reasonable salary</li>
                    <li>• More cash flow flexibility</li>
                    <li>• No payroll processing required on distributions</li>
                  </ul>
                </div>

                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h5 className="font-medium text-amber-800 mb-2 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> Recommended Strategy
                  </h5>
                  <ol className="text-sm text-amber-700 space-y-2">
                    <li><strong>1.</strong> Set salary at 40-60% of expected net profit</li>
                    <li><strong>2.</strong> Pay yourself regularly (monthly/bi-weekly)</li>
                    <li><strong>3.</strong> Take remaining profits as quarterly distributions</li>
                    <li><strong>4.</strong> Document your salary justification</li>
                    <li><strong>5.</strong> Review and adjust annually based on profit changes</li>
                  </ol>
                </div>
              </div>
            </div>

            <Button 
              onClick={() => handleComplete('set_salary')}
              disabled={planningCompleted}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {planningCompleted ? (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> All Complete!</>
              ) : (
                <>I Have a Salary Plan <CheckCircle2 className="w-4 h-4 ml-2" /></>
              )}
            </Button>
          </TabsContent>
        </Tabs>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Progress 
            value={((salaryCompleted ? 1 : 0) + (optimizerCompleted ? 1 : 0) + (planningCompleted ? 1 : 0)) / 3 * 100} 
            className="flex-1 h-2"
          />
          <span className="text-xs text-gray-500">
            {(salaryCompleted ? 1 : 0) + (optimizerCompleted ? 1 : 0) + (planningCompleted ? 1 : 0)}/3 complete
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default SCorpGuide;
